import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../../../api';

const EquipmentChecklist = ({
  formData,
  onChange,
  onSave,
  editId,
  vehicleId, // This is actually equipment_id
}) => {
  const [checklistItems, setChecklistItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const authToken = sessionStorage.getItem('auth_token');
  const userId = sessionStorage.getItem('user_id');

  const handleModalClose = () => setModalVisible(false);
  const handleModalShow = () => setModalVisible(true);

  const fetchChecklist = async () => {
    if (!vehicleId) {
      setError('No equipment ID provided.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get('/ems/equipment-checklist', {
        params: { equipment_id: vehicleId },
        headers: {
          Accept: 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      // API returns: { status: "success", data: [...] }
      const apiData = response.data?.data;

      if (Array.isArray(apiData) && apiData.length > 0) {
        const formattedItems = apiData.map((item) => ({
          id: item.id,
          name: item.name,
          criteria: item.criteria || 1,
          status: false, // Always start unchecked
        }));
        setChecklistItems(formattedItems);
      } else {
        setError('No checklist items found for this equipment.');
        setChecklistItems([]);
      }
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(`Failed to load checklist: ${err.response?.data?.message || err.message}`);
      setChecklistItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicleId && authToken) {
      setChecklistItems([]);
      setError(null);
      fetchChecklist();
      handleModalShow();
    }
  }, [vehicleId, authToken]);

  const handleCheckboxChange = (itemId) => {
    setChecklistItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, status: !item.status } : item
      )
    );
  };

  const handleSubmit = async () => {
    if (!authToken) return alert('Authentication required');
    if (!userId) return alert('User ID not found');
    if (!vehicleId) return alert('Equipment ID is required');
    if (checklistItems.length === 0) return alert('No checklist items to submit');

    try {
      setIsSubmitting(true);

      const checklistData = checklistItems.map((item) => ({
        id: item.id,
        status: item.status ? 1 : 0,
        criteria: item.criteria,
      }));

      const payload = {
        user_id: parseInt(userId, 10),
        equipment_id: parseInt(vehicleId, 10),
        checklist: checklistData,
      };

      await axiosInstance.post('/ems/submit-equipment-checklist', payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      alert('Checklist submitted successfully');
      const modalEl = document.getElementById('bs-daily-checklist');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();
    } catch (err) {
      console.error('Submit error:', err);
      alert(`Failed to submit: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      id="bs-daily-checklist"
      className="modal fade"
      tabIndex="-1"
      aria-labelledby="bs-daily-checklist"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">Daily Checklist</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <form>
              <table className="table text-nowrap mb-0 align-middle mt-3">
                <thead>
                  <tr>
                    <th><h6 className="fw-semibold mb-0">ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Item</h6></th>
                    <th><h6 className="fw-semibold mb-0">Criteria</h6></th>
                    <th><h6 className="fw-semibold mb-0">Check</h6></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        Loading checklist...
                      </td>
                    </tr>
                  ) : checklistItems.length > 0 ? (
                    checklistItems.map((item, index) => (
                      <tr key={`item-${item.id}`}>
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                        <td>
                          <input
                            type="text"
                            className="form-control w-50"
                            value={item.criteria}
                            disabled
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!item.status}
                            onChange={() => handleCheckboxChange(item.id)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-dark py-4">
                        {error || 'No checklist items found for this equipment.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </form>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              data-bs-dismiss="modal"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || checklistItems.length === 0}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Checklist'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentChecklist;